import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Bar, BarChart, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {get, ref} from 'firebase/database';
import {database} from '../../config/firebase';
import {useAuth} from '../../hooks/useAuth';
import {useStatistics} from '../../hooks/useStatistics';
import {ExportService} from '../../services/exportService';

// Couleurs pour les graphiques (thème cohérent avec identité visuelle)
const COLORS = ['#3B82F6', '#10B981', '#FBBF24', '#EF4444', '#8B5CF6', '#EC4899'];

const MobileDashboard = () => {
    const {userRole} = useAuth();
    const [selectedChurchId, setSelectedChurchId] = useState(null);
    const [churches, setChurches] = useState([]);
    const [recentMembers] = useState([]);
    const [setIsSmallScreen] = useState(window.innerWidth < 768);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);

    const {
        statistics,
        statsLoading,
        error,
        getGenderDistributionData,
        getBaptizedDistributionData,
        getProfessionsData,
    } = useStatistics(selectedChurchId);

    const navigate = useNavigate();

    // Gérer le redimensionnement de l'écran
    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setIsSmallScreen]);

    // Récupérer la liste des églises pour l'admin de façon optimisée
    useEffect(() => {
        const fetchChurches = async () => {
            if (userRole?.role === 'admin') {
                try {
                    const churchesRef = ref(database, 'churches');
                    const snapshot = await get(churchesRef);

                    if (snapshot.exists()) {
                        const churchesData = [];
                        snapshot.forEach((childSnapshot) => {
                            churchesData.push({
                                id: childSnapshot.key,
                                ...childSnapshot.val()
                            });
                        });
                        setChurches(churchesData);
                    }
                } catch (error) {
                    console.error("Erreur lors de la récupération des églises:", error);
                }
            } else if (userRole?.role === 'church_admin') {
                // Pour un admin d'église, définir par défaut son église
                setSelectedChurchId(userRole.churchId);
            }

            setLoading(false);
        };

        fetchChurches();
    }, [userRole]);

    const handleExportStats = async () => {
        setExportLoading(true);
        try {
            const result = await ExportService.exportStatsToPDF(statistics, selectedChurchId);
            if (!result.success) {
                throw new Error(result.error);
            }
        } catch (error) {
            alert(`Erreur lors de l'exportation: ${error.message}`);
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportMembers = async () => {
        setExportLoading(true);
        try {
            const result = await ExportService.exportToPDF(selectedChurchId);
            if (!result.success) {
                throw new Error(result.error);
            }
        } catch (error) {
            alert(`Erreur lors de l'exportation: ${error.message}`);
        } finally {
            setExportLoading(false);
        }
    };

    const handleChurchChange = useCallback((churchId) => {
        setSelectedChurchId(churchId === 'all' ? null : churchId);
    }, []);

    const refreshData = useCallback(() => {
        window.location.reload();
    }, []);

    if (loading || statsLoading) {
        return (
            <div className="flex flex-col justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-600">Chargement des données...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 m-4 rounded">
                <div className="flex items-center">
                    <svg className="h-6 w-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"/>
                    </svg>
                    <p className="font-medium">{error}</p>
                </div>
                <button
                    onClick={refreshData}
                    className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    const genderData = getGenderDistributionData();
    const baptizedData = getBaptizedDistributionData();
    const professionsData = getProfessionsData().slice(0, 6); // Top 6 professions

    // Calculer les totaux
    const totalMembers = statistics.global?.totalMembres || 0;
    const currentChurchMembers = selectedChurchId && statistics.church ?
        (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
            statistics.church.totalMembres :
            statistics.church[selectedChurchId]?.totalMembres) || 0 : 0;

    const displayedMembers = selectedChurchId ? currentChurchMembers : totalMembers;
    const displayedMen = selectedChurchId && statistics.church ?
        (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
            statistics.church.hommes :
            statistics.church[selectedChurchId]?.hommes) || 0 :
        statistics.global?.hommes || 0;
    const displayedWomen = selectedChurchId && statistics.church ?
        (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
            statistics.church.femmes :
            statistics.church[selectedChurchId]?.femmes) || 0 :
        statistics.global?.femmes || 0;
    const displayedBaptized = selectedChurchId && statistics.church ?
        (typeof statistics.church === 'object' && !Array.isArray(statistics.church) ?
            statistics.church.baptises :
            statistics.church[selectedChurchId]?.baptises) || 0 :
        statistics.global?.baptises || 0;

    return (
        <div className="mx-auto px-2 sm:px-4 py-4 sm:py-6 bg-gray-50 min-h-screen">
            {/* Header avec filtres et onglets pour mobile */}

            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Tableau de bord</h1>
                </div>

                {/* Sélecteur d'église et boutons d'export pour desktop */}
                <div className="hidden sm:flex justify-between items-center mb-2">
                    {userRole?.role === 'admin' && (
                        <div className="w-64">
                            <select
                                value={selectedChurchId || 'all'}
                                onChange={(e) => handleChurchChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">Toutes les églises</option>
                                {churches.map(church => (
                                    <option key={church.id} value={church.id}>
                                        {church.nom}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="flex gap-2">
                        <button
                            onClick={handleExportStats}
                            disabled={exportLoading}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm"
                        >
                            {exportLoading ? (
                                <div
                                    className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path fillRule="evenodd"
                                          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                          clipRule="evenodd"/>
                                </svg>
                            )}
                            Stats
                        </button>
                        <button
                            onClick={handleExportMembers}
                            disabled={exportLoading}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm"
                        >
                            {exportLoading ? (
                                <div
                                    className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path fillRule="evenodd"
                                          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                          clipRule="evenodd"/>
                                </svg>
                            )}
                            Membres
                        </button>
                    </div>
                </div>

                {/* Onglets pour mobile */}
                <div className="sm:hidden flex overflow-x-auto pb-2 mt-4 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 whitespace-nowrap font-medium ${
                            activeTab === 'overview'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Aperçu
                    </button>
                    <button
                        onClick={() => setActiveTab('graphs')}
                        className={`px-4 py-2 whitespace-nowrap font-medium ${
                            activeTab === 'graphs'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Graphiques
                    </button>
                    <button
                        onClick={() => setActiveTab('actions')}
                        className={`px-4 py-2 whitespace-nowrap font-medium ${
                            activeTab === 'actions'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Actions
                    </button>
                </div>

                {/* Sélecteur d'église et boutons d'export pour mobile */}
                <div className="sm:hidden mt-4">
                    {userRole?.role === 'admin' && activeTab === 'overview' && (
                        <div className="w-full">
                            <select
                                value={selectedChurchId || 'all'}
                                onChange={(e) => handleChurchChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">Toutes les églises</option>
                                {churches.map(church => (
                                    <option key={church.id} value={church.id}>
                                        {church.nom}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {activeTab === 'actions' && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <button
                                onClick={handleExportStats}
                                disabled={exportLoading}
                                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                            >
                                {exportLoading ? (
                                    <div
                                        className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path fillRule="evenodd"
                                              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                              clipRule="evenodd"/>
                                    </svg>
                                )}
                                Exporter stats
                            </button>
                            <button
                                onClick={handleExportMembers}
                                disabled={exportLoading}
                                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
                            >
                                {exportLoading ? (
                                    <div
                                        className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path fillRule="evenodd"
                                              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                              clipRule="evenodd"/>
                                    </svg>
                                )}
                                Exporter membres
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Contenu principal - version desktop toujours visible */}
            <div className="hidden sm:block">
                {/* Cards de statistiques */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 font-medium mb-1">Total des membres</p>
                                <h2 className="text-2xl font-bold text-gray-800">{displayedMembers}</h2>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 font-medium mb-1">Hommes</p>
                                <h2 className="text-2xl font-bold text-gray-800">{displayedMen}</h2>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M6 3v12h12M8 9h4m4 0h-4m-4 0V5m4 4v4"/>
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 font-medium mb-1">Femmes</p>
                                <h2 className="text-2xl font-bold text-gray-800">{displayedWomen}</h2>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full bg-amber-100 text-amber-600 mr-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500 font-medium mb-1">Baptisés</p>
                                <h2 className="text-2xl font-bold text-gray-800">{displayedBaptized}</h2>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section avec Graphiques et Activité récente */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    {/* Répartition par sexe */}
                    <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <h2 className="text-lg font-semibold mb-4 text-gray-800">Répartition par sexe</h2>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={genderData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                >
                                    {genderData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => `${value} membres`}/>
                                <Legend/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Répartition baptisés/non-baptisés */}
                    <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <h2 className="text-lg font-semibold mb-4 text-gray-800">Baptisés / Non-baptisés</h2>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={baptizedData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                >
                                    {baptizedData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => `${value} membres`}/>
                                <Legend/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Répartition par profession */}
                    <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <h2 className="text-lg font-semibold mb-4 text-gray-800">Principales professions</h2>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={professionsData} layout="vertical" margin={{left: 80}}>
                                <XAxis type="number"/>
                                <YAxis type="category" dataKey="name" width={80}/>
                                <Tooltip/>
                                <Bar dataKey="value" name="Membres" fill="#3B82F6"/>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Membres récents et activité */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <h2 className="text-lg font-semibold mb-4 text-gray-800">Membres récemment inscrits</h2>
                        {recentMembers.length > 0 ? (
                            <div className="divide-y divide-gray-200">
                                {recentMembers.map(member => (
                                    <div key={member.id} className="py-3 flex justify-between items-center">
                                        <div className="flex items-center">
                                            <div
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${member.sexe === 'Homme' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'}`}>
                                                {member.prenom.charAt(0).toUpperCase()}{member.nom.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm font-medium text-gray-900">{member.prenom} {member.nom}</p>
                                                <p className="text-xs text-gray-500">{member.profession || 'Non spécifié'}</p>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {new Date(member.createdAt).toLocaleDateString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-gray-500 py-4">Aucun membre récent</p>
                        )}
                        <div className="mt-4 text-right">
                            <button
                                onClick={() => navigate('/members')}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                Voir tous les membres →
                            </button>
                        </div>
                    </div>
                </div>

                {/* Actions rapides */}
                <div className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow mb-6">
                    <h2 className="text-lg font-semibold mb-4 text-gray-800">Actions rapides</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <button
                            onClick={() => navigate('/members/add')}
                            className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors flex flex-col items-center justify-center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mb-2" viewBox="0 0 20 20"
                                 fill="currentColor">
                                <path
                                    d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"/>
                            </svg>
                            Ajouter un membre
                        </button>

                        <button
                            onClick={() => navigate('/members')}
                            className="p-4 bg-gradient-to-br from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors flex flex-col items-center justify-center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mb-2" viewBox="0 0 20 20"
                                 fill="currentColor">
                                <path
                                    d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                            </svg>
                            Gérer les membres
                        </button>

                        <button
                            onClick={() => navigate('/messages')}
                            className="p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-colors flex flex-col items-center justify-center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mb-2" viewBox="0 0 20 20"
                                 fill="currentColor">
                                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                                <path
                                    d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/>
                            </svg>
                            Envoyer un message
                        </button>

                        {userRole?.role === 'admin' && (
                            <button
                                onClick={() => navigate('/churches')}
                                className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-colors flex flex-col items-center justify-center"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mb-2" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path
                                        d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                                </svg>
                                Gérer les églises
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Contenu principal - version mobile avec onglets */}
            <div className="sm:hidden">
                {/* Onglet Aperçu */}
                {activeTab === 'overview' && (
                    <div className="space-y-4">
                        {/* Cards de statistiques en grille 2x2 */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                                <div className="flex flex-col items-center">
                                    <div className="p-2 rounded-full bg-blue-100 text-blue-600 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                                             viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 font-medium text-center">Total</p>
                                    <h2 className="text-xl font-bold text-gray-800">{displayedMembers}</h2>
                                </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg shadow-sm">
                                <div className="flex flex-col items-center">
                                    <div className="p-2 rounded-full bg-green-100 text-green-600 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                                             viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 font-medium text-center">Hommes</p>
                                    <h2 className="text-xl font-bold text-gray-800">{displayedMen}</h2>
                                </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg shadow-sm">
                                <div className="flex flex-col items-center">
                                    <div className="p-2 rounded-full bg-purple-100 text-purple-600 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                                             viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M6 3v12h12M8 9h4m4 0h-4m-4 0V5m4 4v4"/>
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 font-medium text-center">Femmes</p>
                                    <h2 className="text-xl font-bold text-gray-800">{displayedWomen}</h2>
                                </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg shadow-sm">
                                <div className="flex flex-col items-center">
                                    <div className="p-2 rounded-full bg-amber-100 text-amber-600 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                                             viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                        </svg>
                                    </div>
                                    <p className="text-xs text-gray-500 font-medium text-center">Baptisés</p>
                                    <h2 className="text-xl font-bold text-gray-800">{displayedBaptized}</h2>
                                </div>
                            </div>
                        </div>

                        {/* Graphiques condensés - un seul pour aperçu */}
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <h2 className="text-sm font-semibold mb-3 text-gray-800">Répartition par sexe</h2>
                            <ResponsiveContainer width="100%" height={180}>
                                <PieChart>
                                    <Pie
                                        data={genderData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={60}
                                        fill="#8884d8"
                                        dataKey="value"
                                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {genderData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `${value} membres`}/>
                                    <Legend/>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Actions rapides condensées */}
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <h2 className="text-sm font-semibold mb-3 text-gray-800">Actions rapides</h2>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={() => navigate('/members/add')}
                                    className="p-2 bg-blue-600 text-white rounded-lg flex flex-col items-center"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1"
                                         viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path
                                            d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"/>
                                    </svg>
                                    <span className="text-xs">Ajouter</span>
                                </button>

                                <button
                                    onClick={() => navigate('/members')}
                                    className="p-2 bg-gray-600 text-white rounded-lg flex flex-col items-center"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1"
                                         viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path
                                            d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                                    </svg>
                                    <span className="text-xs">Membres</span>
                                </button>

                                <button
                                    disabled={true}
                                    onClick={() => navigate('/messages')}
                                    className="p-2 bg-green-600 text-white rounded-lg flex flex-col items-center"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1"
                                         viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path
                                            d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                                        <path
                                            d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/>
                                    </svg>
                                    <span className="text-xs">Message</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Onglet Graphiques */}
                {activeTab === 'graphs' && (
                    <div className="space-y-4">
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <h2 className="text-sm font-semibold mb-3 text-gray-800">Répartition par sexe</h2>
                            <ResponsiveContainer width="100%" height={180}>
                                <PieChart>
                                    <Pie
                                        data={genderData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={60}
                                        fill="#8884d8"
                                        dataKey="value"
                                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {genderData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `${value} membres`}/>
                                    <Legend/>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <h2 className="text-sm font-semibold mb-3 text-gray-800">Baptisés / Non-baptisés</h2>
                            <ResponsiveContainer width="100%" height={180}>
                                <PieChart>
                                    <Pie
                                        data={baptizedData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={60}
                                        fill="#8884d8"
                                        dataKey="value"
                                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {baptizedData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `${value} membres`}/>
                                    <Legend/>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white p-3 rounded-lg shadow-sm">
                            <h2 className="text-sm font-semibold mb-3 text-gray-800">Principales professions</h2>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={professionsData} layout="vertical" margin={{left: 80}}>
                                    <XAxis type="number"/>
                                    <YAxis type="category" dataKey="name" width={80}/>
                                    <Tooltip/>
                                    <Bar dataKey="value" name="Membres" fill="#3B82F6"/>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Onglet Actions */}
                {activeTab === 'actions' && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => navigate('/members/add')}
                                className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors flex flex-col items-center justify-center"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path
                                        d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"/>
                                </svg>
                                <span className="text-sm">Ajouter un membre</span>
                            </button>

                            <button
                                onClick={() => navigate('/members')}
                                className="p-4 bg-gradient-to-br from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors flex flex-col items-center justify-center"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path
                                        d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                                </svg>
                                <span className="text-sm">Gérer les membres</span>
                            </button>

                            <button
                                onClick={() => navigate('/messages')}
                                className="p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-colors flex flex-col items-center justify-center"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" viewBox="0 0 20 20"
                                     fill="currentColor">
                                    <path
                                        d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                                    <path
                                        d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/>
                                </svg>
                                <span className="text-sm">Envoyer un message</span>
                            </button>

                            {userRole?.role === 'admin' && (
                                <button
                                    onClick={() => navigate('/churches')}
                                    className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-colors flex flex-col items-center justify-center"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2"
                                         viewBox="0 0 20 20"
                                         fill="currentColor">
                                        <path
                                            d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                                    </svg>
                                    <span className="text-sm">Gérer les églises</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer avec date de mise à jour */}
            <div className="text-right text-xs text-gray-400 mt-6">
                Dernière mise à jour: {new Date().toLocaleString()}
            </div>
        </div>
    );
};

export default MobileDashboard;
